import { db } from '../firebase';
import {
    collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
    query, where, onSnapshot
} from 'firebase/firestore';

const COLLECTION_USERS = 'users';

export const userService = {
    async saveUser(user, role) {
        try {
            const userRef = doc(db, COLLECTION_USERS, user.uid);
            // Derive email safely: explicit email, else a username that looks like one.
            const email = user.email
                || (typeof user.username === 'string' && user.username.includes('@') ? user.username : null);
            const username = user.username || user.displayName || (email ? email.split('@')[0] : user.uid);
            const userData = {
                uid: user.uid,
                username,
                // Respect a role/allowedLocations supplied on the user object (admin flows pass
                // them inline); the `role` arg wins when given; fall back to lowest privilege.
                role: role || user.role || 'monteur',
                allowedLocations: user.allowedLocations || [],
                // Preserve the original creation date on edits (merge writes) instead of resetting it.
                createdAt: user.createdAt || new Date().toISOString()
            };
            if (email) userData.email = email;
            // Carry through optional profile fields only when the caller provides them.
            for (const key of ['name', 'displayName', 'emailNotificationsEnabled', 'linkedPersonnelRoleId']) {
                if (user[key] !== undefined) userData[key] = user[key];
            }
            await setDoc(userRef, userData, { merge: true });
            return userData;
        } catch (error) {
            console.error('[UserService] saveUser failed:', error);
            throw error;
        }
    },

    async getUserProfile(uid) {
        try {
            const userRef = doc(db, COLLECTION_USERS, uid);
            const docSnap = await getDoc(userRef);
            if (docSnap.exists()) {
                return docSnap.data();
            } else {
                return null;
            }
        } catch (error) {
            console.error('[UserService] getUserProfile failed:', error);
            throw error;
        }
    },

    // scope: null = alles (superuser), [] = niets, anders die locaties.
    // Per locatie een array-contains-query en daarna ontdubbelen op uid,
    // want een gebruiker kan bij meerdere locaties horen. De regels staan
    // een collectiebrede query alleen de superuser toe.
    async getAllUsers(scope = null) {
        try {
            const col = collection(db, COLLECTION_USERS);
            if (scope === null) {
                const querySnapshot = await getDocs(col);
                return querySnapshot.docs.map(d => d.data());
            }
            if (scope.length === 0) return [];
            const snaps = await Promise.all(scope.map(loc =>
                getDocs(query(col, where('allowedLocations', 'array-contains', loc)))
            ));
            const perUid = new Map();
            snaps.forEach(s => s.docs.forEach(d => perUid.set(d.data().uid || d.id, d.data())));
            return [...perUid.values()];
        } catch (error) {
            console.error('[UserService] getAllUsers failed:', error);
            throw error;
        }
    },

    async getAllUsersByRole(role) {
        try {
            const q = query(collection(db, COLLECTION_USERS), where("role", "==", role));
            const querySnapshot = await getDocs(q);
            const users = [];
            querySnapshot.forEach((doc) => {
                users.push(doc.data());
            });
            return users;
        } catch (error) {
            console.error('[UserService] getAllUsersByRole failed:', error);
            throw error;
        }
    },

    async updateUserLocations(uid, locations) {
        try {
            const userRef = doc(db, COLLECTION_USERS, uid);
            await updateDoc(userRef, {
                allowedLocations: locations
            });
        } catch (error) {
            console.error('[UserService] updateUserLocations failed:', error);
            throw error;
        }
    },

    async updateUserRole(uid, newRole) {
        try {
            const userRef = doc(db, COLLECTION_USERS, uid);
            await updateDoc(userRef, {
                role: newRole
            });
        } catch (error) {
            console.error('[UserService] updateUserRole failed:', error);
            throw error;
        }
    },

    async deleteUser(uid) {
        try {
            // Note: This only deletes the profile document, not the Auth account
            await deleteDoc(doc(db, COLLECTION_USERS, uid));
        } catch (error) {
            console.error('[UserService] deleteUser failed:', error);
            throw error;
        }
    },

    subscribeToUserProfile(uid, callback) {
        const userRef = doc(db, COLLECTION_USERS, uid);
        return onSnapshot(userRef, (doc) => {
            if (doc.exists()) {
                callback(doc.data());
            } else {
                callback(null);
            }
        }, (error) => {
            console.error('[UserService] subscribeToUserProfile error:', error);
            callback(null);
        });
    }
};
